import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useMembership } from "../lib/membership";
import MapView from "../components/MapView";
import LocationTracker, { useLocationPermission } from "../components/LocationTracker";
import BottomNav from "../components/BottomNav";
import ContactSheet from "../components/ContactSheet";
import { Crosshair, Plus, MapPin, Radar, X, Pencil, BellRing, Navigation, Heart, LayoutGrid, ChevronDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useBrowserNotifications, useAlertNotifier } from "../lib/notifications";
import { openDirections } from "../lib/directions";
import { isFamilyContact } from "../lib/colors";
import { useProximityWorker } from "../lib/useProximityWorker";

export default function Dashboard() {
  const { user } = useAuth();
  const { isEliteMaster, groups } = useMembership();
  const { permission, request: requestNotif } = useBrowserNotifications();
  useAlertNotifier({ enabled: permission === "granted" });
  const [search, setSearch] = useSearchParams();
  const geoPermission = useLocationPermission();
  // Rehydrate last-known position from localStorage so the ME triangle
  // is visible IMMEDIATELY on app launch — before the OS delivers a fresh
  // GPS fix (which can take 5-30s outdoors and longer indoors).
  const [me, setMe] = useState(() => {
    try {
      const raw = localStorage.getItem("proximity:last-fix");
      if (!raw) return null;
      const v = JSON.parse(raw);
      if (typeof v?.lat !== "number" || typeof v?.lng !== "number") return null;
      // Only trust cached fixes < 24h old; older is misleading
      if (v.savedAt && Date.now() - v.savedAt > 24 * 60 * 60 * 1000) return null;
      return { lat: v.lat, lng: v.lng, accuracy: v.accuracy, stale: true };
    } catch { return null; }
  });
  const [contacts, setContacts] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [adding, setAdding] = useState(false);
  const [pendingPoint, setPendingPoint] = useState(null);
  const [zoneName, setZoneName] = useState("");
  const [zoneRadius, setZoneRadius] = useState(200);
  const [alertsCount, setAlertsCount] = useState(0);
  const [familyOnly, setFamilyOnly] = useState(false);
  const [viewMode, setViewMode] = useState("all"); // "personal" | "all" | group_id
  const [viewMenu, setViewMenu] = useState(false);
  const [openContact, setOpenContact] = useState(null); // for double-tap sheet
  const [groupMembers, setGroupMembers] = useState({}); // {group_id: [user_ids]}
  const [locationRequestKey, setLocationRequestKey] = useState(0);
  const [backgroundLocationChoice, setBackgroundLocationChoice] = useState(() => {
    try { return localStorage.getItem("proximity:background-location") || null; }
    catch { return null; }
  });
  const [backgroundLocationStatus, setBackgroundLocationStatus] = useState(null);
  const [mapDiagnostics, setMapDiagnostics] = useState({ ready: false, meMarker: false, contactsWithCoordinates: 0 });

  const loadAll = useCallback(async () => {
    try {
      const [a, b, c, al] = await Promise.all([
        api.get("/locations/me"),
        api.get("/contacts"),
        api.get("/geofences"),
        api.get("/alerts"),
      ]);
      setMe(a.data && a.data.lat != null && a.data.lng != null ? a.data : null);
      setContacts(b.data || []);
      setGeofences(c.data || []);
      setAlertsCount((al.data || []).filter((x) => !x.read).length);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 5000);
    const onFocus = () => loadAll();
    window.addEventListener("focus", onFocus);
    // Immediately request a one-shot position so the map can center on the user
    // even before LocationTracker's watcher fires the first update.
    if (!Capacitor.isNativePlatform() && "geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setMe((cur) => cur || {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        () => {},
        { enableHighAccuracy: true, maximumAge: 60000, timeout: 8000 }
      );
    }
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [loadAll]);

  const onLocalUpdate = useCallback((pos) => {
    const next = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
    setMe(next);
    // Persist so the next launch can show the ME triangle instantly instead
    // of a stale "Locating you…" banner while the GPS chip warms back up.
    try {
      localStorage.setItem("proximity:last-fix", JSON.stringify({ ...next, savedAt: Date.now() }));
    } catch { /* storage disabled — non-fatal */ }
  }, []);

  const requestPreciseLocation = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      try { localStorage.setItem("proximity:foreground-location", "enabled"); } catch {}
      window.dispatchEvent(new CustomEvent("proximity:foreground-location", { detail: { enabled: true } }));
      navigator.geolocation?.getCurrentPosition(
        onLocalUpdate,
        () => {},
        { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
      );
    }
    setLocationRequestKey((value) => value + 1);
  }, [onLocalUpdate]);

  const chooseBackgroundLocation = useCallback((enabled) => {
    const choice = enabled ? "enabled" : "declined";
    try { localStorage.setItem("proximity:background-location", choice); } catch {}
    setBackgroundLocationChoice(choice);
    if (enabled) {
      try { localStorage.setItem("proximity:foreground-location", "enabled"); } catch {}
      window.dispatchEvent(new CustomEvent("proximity:foreground-location", { detail: { enabled: true } }));
    }
    window.dispatchEvent(new CustomEvent("proximity:background-location", { detail: { enabled } }));
  }, []);

  useEffect(() => {
    const onStatus = (event) => setBackgroundLocationStatus(event?.detail || null);
    window.addEventListener("proximity:background-location-status", onStatus);
    return () => window.removeEventListener("proximity:background-location-status", onStatus);
  }, []);

  const onMapClick = (latlng) => {
    if (!adding) return;
    setPendingPoint(latlng);
  };

  const saveGeofence = async () => {
    if (!pendingPoint || !zoneName.trim()) return;
    try {
      await api.post("/geofences", {
        name: zoneName.trim(),
        lat: pendingPoint.lat,
        lng: pendingPoint.lng,
        radius_m: Number(zoneRadius) || 200,
      });
      toast.success("Safe zone created");
      setAdding(false);
      setPendingPoint(null);
      setZoneName("");
      setZoneRadius(200);
      loadAll();
    } catch (e) {
      toast.error("Failed to create zone");
    }
  };

  // Off-thread proximity math — runs Haversine in a Web Worker so the WebGL
  // map keeps rendering smoothly. Falls back to server-provided distances
  // if the worker hasn't emitted a result yet.
  const { distances: liveDistances, inside: liveInsideIds, onAlert } = useProximityWorker({
    me,
    contacts,
    radiusMiles: 3,
  });

  // "Live · N nearby" — prefer worker distances; fall back to server-provided.
  const nearbyContacts = liveInsideIds.size > 0
    ? contacts.filter((c) => liveInsideIds.has(c.user_id))
    : contacts.filter((c) => c.distance_miles != null && c.distance_miles <= 3);

  // Toast when a contact CROSSES into 3-mile range (deduped by boundary crossing).
  useEffect(() => {
    return onAlert(({ kind, user_id, distance_miles }) => {
      if (kind !== "entered") return;
      const c = contacts.find((x) => x.user_id === user_id);
      if (!c) return;
      toast.success(`${c.name || "A contact"} is nearby`, {
        description: distance_miles != null
          ? (distance_miles < 0.19
              ? `${Math.round(distance_miles * 5280)} ft away`
              : `${distance_miles.toFixed(2)} mi away`)
          : "within 3 miles",
      });
    });
  }, [contacts, onAlert]);

  // Apply view filter (personal/all/specific group)
  let viewContacts = contacts;
  if (isEliteMaster) {
    if (viewMode === "personal") {
      viewContacts = [];
    } else if (viewMode !== "all" && groupMembers[viewMode]) {
      const set = new Set(groupMembers[viewMode]);
      viewContacts = contacts.filter((c) => set.has(c.user_id));
    }
  }
  const visibleContacts = (familyOnly ? viewContacts.filter(isFamilyContact) : viewContacts).map((c) => {
    const d = liveDistances.get(c.user_id);
    return d != null ? { ...c, distance_miles: d } : c;
  });

  // Lazy-load group members when an Elite master picks a group view
  useEffect(() => {
    if (!isEliteMaster) return;
    if (viewMode === "personal" || viewMode === "all") return;
    if (groupMembers[viewMode]) return;
    (async () => {
      try {
        const r = await api.get(`/groups/${viewMode}`);
        setGroupMembers((prev) => ({ ...prev, [viewMode]: (r.data?.devices || []).map((d) => d.user_id) }));
      } catch {}
    })();
  }, [viewMode, isEliteMaster, groupMembers]);

  const currentViewLabel =
    viewMode === "personal" ? "Personal" :
    viewMode === "all" ? "All Devices" :
    (groups.find((g) => g.group_id === viewMode)?.name || "Group");

  // Support /dashboard?focus=<user_id> (contact name click deep-link)
  const focusId = search.get("focus");
  const focusContact = focusId ? contacts.find((c) => c.user_id === focusId) : null;
  const focusUser = focusContact?.latest
    ? { key: focusContact.user_id, lat: focusContact.latest.lat, lng: focusContact.latest.lng }
    : null;

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-zinc-950 text-zinc-50">
      <LocationTracker
        enabled={user?.share_location !== false}
        onUpdate={onLocalUpdate}
        requestKey={locationRequestKey}
      />

      {/* Map full screen */}
      <div className="absolute inset-0 z-0">
        <MapView
          me={me}
          contacts={visibleContacts}
          geofences={[...geofences, ...(pendingPoint ? [{
            geofence_id: "pending",
            name: zoneName || "New zone",
            lat: pendingPoint.lat, lng: pendingPoint.lng,
            radius_m: Number(zoneRadius) || 200,
            color: "#FFCC00",
          }] : [])]}
          onMapClick={onMapClick}
          onContactOpen={(c) => setOpenContact(c)}
          onRenderState={setMapDiagnostics}
          focusUser={focusUser}
          highlightUserId={search.get("focus") || undefined}
          height="100vh"
        />
      </div>

      {/* Top status card */}
      <div className="absolute top-4 left-4 right-4 z-20 flex items-start gap-3">
        <div data-testid="status-card" className="glass rounded-2xl px-4 py-3 flex items-center gap-3 flex-1 max-w-sm">
          <img src="/proximity-logo.png" alt="" className="size-9 rounded-full object-contain bg-white/5"/>
          <div className="min-w-0">
            <div className="label-eyebrow">Live · {nearbyContacts.length} nearby</div>
            <div className="font-display text-lg font-bold truncate">
              {user?.name || "You"}
            </div>
          </div>
        </div>
        <Link
          to="/alerts"
          data-testid="alerts-pill"
          className="glass rounded-full px-4 py-3 flex items-center gap-2 font-semibold text-sm hover:bg-zinc-900/80 transition-colors"
        >
          <span className="size-2 rounded-full bg-[#FF3B30] animate-pulse" />
          <span>{alertsCount}</span>
        </Link>
      </div>

      {/* Elite view switcher */}
      {isEliteMaster && (
        <div className="absolute top-20 left-4 z-20">
          <div className="relative">
            <button
              data-testid="view-switcher"
              onClick={() => setViewMenu((v) => !v)}
              className="glass rounded-full px-3 py-2 inline-flex items-center gap-2 text-xs font-bold hover:bg-zinc-900/80"
            >
              <LayoutGrid size={12} className="text-[#FFCC00]"/>
              <span className="uppercase tracking-wide">{currentViewLabel}</span>
              <ChevronDown size={12}/>
            </button>
            {viewMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setViewMenu(false)}/>
                <div className="absolute left-0 top-11 z-40 w-60 p-1.5 rounded-2xl border border-white/10 bg-zinc-950/95 backdrop-blur shadow-2xl">
                  <ViewOpt active={viewMode === "personal"} onClick={() => { setViewMode("personal"); setViewMenu(false); }} testid="view-personal">Personal Map</ViewOpt>
                  <ViewOpt active={viewMode === "all"} onClick={() => { setViewMode("all"); setViewMenu(false); }} testid="view-all">All Devices</ViewOpt>
                  {groups.length > 0 && <div className="my-1 h-px bg-white/5"/>}
                  {groups.map((g) => (
                    <ViewOpt
                      key={g.group_id}
                      active={viewMode === g.group_id}
                      onClick={() => { setViewMode(g.group_id); setViewMenu(false); }}
                      testid={`view-group-${g.group_id}`}
                      dot={g.is_active ? "#34C759" : "#52525b"}
                    >
                      {g.name}
                    </ViewOpt>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Right-side action stack */}
      <div className="absolute right-4 top-24 z-20 flex flex-col gap-3">
        <button
          data-testid="recenter-btn"
          onClick={() => me && window.dispatchEvent(new CustomEvent("recenter"))}
          className="glass size-12 rounded-full grid place-items-center hover:bg-zinc-900/80"
          title="Recenter"
        >
          <MapPin size={18} />
        </button>
        <button
          data-testid="family-only-btn"
          onClick={() => setFamilyOnly((v) => !v)}
          className={`size-12 rounded-full grid place-items-center transition-colors ${
            familyOnly ? "bg-[#FF2D55] text-white" : "glass hover:bg-zinc-900/80 text-zinc-300"
          }`}
          title={familyOnly ? "Show everyone" : "Show family only"}
        >
          <Heart size={18} fill={familyOnly ? "#fff" : "none"} />
        </button>
      </div>

      {/* Adding hint */}
      {adding && !pendingPoint && (
        <div className="absolute left-1/2 -translate-x-1/2 top-24 z-20 glass rounded-full px-4 py-2 text-sm">
          <span className="label-eyebrow mr-2">Tap on map</span>
          <span>to place a safe zone</span>
        </div>
      )}

      {/* Foreground location is requested only after this clear explanation.
          Background sharing is a separate opt-in below. */}
      {geoPermission === "prompt" && (
        <div className="absolute left-4 right-4 bottom-24 z-20">
          <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3 border border-[#FFCC00]/30">
            <div className="size-9 rounded-full bg-[#FFCC00]/15 grid place-items-center shrink-0">
              <AlertTriangle size={16} className="text-[#FFCC00]"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">Enable precise location</div>
              <div className="text-[11px] text-zinc-400">Proximity uses precise GPS to show you on the map and share only with people you accept.</div>
            </div>
            <button data-testid="request-precise-location" onClick={requestPreciseLocation} className="shrink-0 rounded-full bg-[#007AFF] px-3 py-2 text-xs font-bold text-white">Allow</button>
          </div>
        </div>
      )}
      {geoPermission === "denied" && (
        <div className="absolute left-4 right-4 bottom-24 z-20">
          <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3 border border-[#FFCC00]/30">
            <div className="size-9 rounded-full bg-[#FFCC00]/15 grid place-items-center shrink-0">
              <AlertTriangle size={16} className="text-[#FFCC00]"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">Precise location is off</div>
              <div className="text-[11px] text-zinc-400">Open Android Settings → Proximity → Permissions and allow precise location, then return here.</div>
            </div>
            <button data-testid="retry-precise-location" onClick={requestPreciseLocation} className="shrink-0 rounded-full border border-white/15 px-3 py-2 text-xs font-bold">Retry</button>
          </div>
        </div>
      )}

      {Capacitor.isNativePlatform() && (geoPermission === "granted" || Boolean(me)) && user?.share_location !== false && !backgroundLocationChoice && (
        <div className="absolute left-4 right-4 bottom-24 z-20">
          <div className="glass rounded-2xl px-4 py-3 border border-[#007AFF]/30">
            <div className="font-bold text-sm">Keep sharing when Proximity is closed?</div>
            <div className="mt-1 text-[11px] text-zinc-400">Optional background location lets your accepted circle receive updates while the app is not open.</div>
            <div className="mt-3 flex gap-2">
              <button data-testid="enable-background-location" onClick={() => chooseBackgroundLocation(true)} className="flex-1 rounded-full bg-[#007AFF] px-3 py-2 text-xs font-bold text-white">Enable background sharing</button>
              <button data-testid="decline-background-location" onClick={() => chooseBackgroundLocation(false)} className="rounded-full border border-white/15 px-3 py-2 text-xs font-bold">Not now</button>
            </div>
          </div>
        </div>
      )}

      {Capacitor.isNativePlatform() && backgroundLocationChoice === "enabled" &&
       ["permission_denied", "start_failed", "upload_failed", "signin_required", "waiting_for_precise_fix", "location_error"].includes(backgroundLocationStatus?.status) && (
        <div className="absolute left-4 right-4 bottom-24 z-20">
          <div data-testid="background-location-warning" className="glass rounded-2xl px-4 py-3 border border-[#FF3B30]/35">
            <div className="font-bold text-sm">Background sharing needs attention</div>
            <div className="mt-1 text-[11px] text-zinc-400">
              {backgroundLocationStatus.status === "permission_denied"
                ? "Android has not granted background location. Open Proximity permissions and choose Allow all the time with Precise location on."
                : backgroundLocationStatus.status === "signin_required"
                  ? "Your session expired. Sign in again to resume location sharing."
                  : backgroundLocationStatus.status === "waiting_for_precise_fix"
                    ? "Android is providing only an imprecise location. Turn on Precise location for Proximity."
                  : backgroundLocationStatus.status === "upload_failed"
                    ? "Proximity has a GPS fix but cannot reach the server. It will retry on the next fix."
                    : "Android could not start background location. Check permissions and battery settings, then reopen Proximity."}
            </div>
          </div>
        </div>
      )}

      {(process.env.NODE_ENV !== "production" || process.env.REACT_APP_MAP_DIAGNOSTICS === "true") && (
        <div data-testid="map-diagnostics" className="absolute bottom-3 left-4 z-20 rounded-lg bg-black/75 px-3 py-2 font-mono text-[10px] text-zinc-300">
          GPS: {me ? "fix" : "waiting"} · Me marker: {mapDiagnostics.meMarker ? "rendered" : "pending"} · Contact markers: {mapDiagnostics.contactsWithCoordinates} · Map: {mapDiagnostics.ready ? "ready" : "loading"}
        </div>
      )}

      {/* Waiting-for-fix banner — helps users understand why their ME pin
          hasn't appeared yet. Only shown when permission is granted (or the
          user hasn't been asked yet) but no fix has arrived. */}
      {!me && geoPermission !== "denied" && (
        <div data-testid="waiting-for-gps" className="absolute left-4 right-4 bottom-24 z-20">
          <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3 border border-[#007AFF]/30">
            <div className="size-9 rounded-full bg-[#007AFF]/15 grid place-items-center shrink-0">
              <Crosshair size={16} className="text-[#007AFF] animate-pulse"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">Locating you…</div>
              <div className="text-[11px] text-zinc-400">Waiting for a precise GPS fix. This can take up to 30 seconds outdoors and longer indoors.</div>
            </div>
          </div>
        </div>
      )}

      {/* New safe zone editor — only shown when placing a geofence */}
      {pendingPoint && (
        <div className="absolute left-4 right-4 bottom-24 z-20">
          <div className="glass rounded-2xl p-4">
            <div className="label-eyebrow mb-2">New safe zone</div>
            <input
              data-testid="zone-name-input"
              placeholder="e.g. Home, School, Office"
              value={zoneName}
              onChange={(e) => setZoneName(e.target.value)}
              className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007AFF]"
            />
            <div className="mt-3 flex items-center gap-3">
              <span className="label-eyebrow">Radius</span>
              <input
                type="range" min="50" max="2000" step="50"
                value={zoneRadius}
                onChange={(e) => setZoneRadius(e.target.value)}
                className="flex-1 accent-[#007AFF]"
              />
              <span className="font-mono text-sm w-16 text-right">{zoneRadius}m</span>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                data-testid="save-zone-btn"
                onClick={saveGeofence}
                className="flex-1 bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold py-2.5 rounded-lg"
              >
                Save zone
              </button>
              <button
                onClick={() => { setPendingPoint(null); setAdding(false); }}
                className="px-4 py-2.5 rounded-lg border border-white/10 hover:bg-white/5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact management sheet (opens on double-tap of a marker) */}
      {openContact && (
        <ContactSheet
          contact={openContact}
          onClose={() => setOpenContact(null)}
          onChanged={() => loadAll()}
        />
      )}

      <BottomNav />
    </div>
  );
}

function ViewOpt({ active, onClick, testid, children, dot }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left hover:bg-white/5 ${active ? "bg-[#007AFF]/15 text-[#007AFF]" : ""}`}
    >
      {dot && <span className="size-2 rounded-full" style={{ background: dot }}/>}
      <span className="flex-1 truncate font-semibold">{children}</span>
      {active && <span className="text-[10px] font-black uppercase">On</span>}
    </button>
  );
}
